import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import * as Dialog from '@radix-ui/react-dialog';
import { observer } from 'mobx-react-lite';
import { useMediaStore } from '@/hooks/useStores';
import { getApiConfiguration } from '@/services/SessionService';
import type { MediaExportJobResponse } from 'we-tree-api-client/v2';
import { WeTreeApiError } from '@/api/errors';

import './export-media-modal.css';

export interface ExportMediaModalProps {
  familyId: string;
  isOpen: boolean;
  onClose: () => void;
}

type Phase = 'confirm' | 'in_progress' | 'ready' | 'failed' | 'expired';

function formatBytes(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  if (bytes < 1024 * 1024 * 1024) return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  return `${(bytes / (1024 * 1024 * 1024)).toFixed(2)} GB`;
}

function errorMessage(err: unknown, fallback: string, storeFallback: string | null): string {
  if (err instanceof WeTreeApiError) return err.getUserMessage();
  return storeFallback || fallback;
}

/**
 * Owner/admin flow to request a rate-limited bulk media ZIP (free-tier carve-out).
 * Starts only after the user confirms — does not use frozen per-asset play/download endpoints.
 */
const ExportMediaModal: React.FC<ExportMediaModalProps> = observer(({ familyId, isOpen, onClose }) => {
  const { t } = useTranslation('forms');
  const { t: tCommon } = useTranslation('common');
  const mediaStore = useMediaStore();
  const [phase, setPhase] = useState<Phase>('confirm');
  const [job, setJob] = useState<MediaExportJobResponse | null>(null);
  const [phaseError, setPhaseError] = useState<string | null>(null);
  const [downloading, setDownloading] = useState(false);
  const pollRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const cancelledRef = useRef(false);

  const stopPolling = useCallback(() => {
    if (pollRef.current) {
      clearInterval(pollRef.current);
      pollRef.current = null;
    }
  }, []);

  const reset = useCallback(() => {
    cancelledRef.current = true;
    stopPolling();
    setPhase('confirm');
    setJob(null);
    setPhaseError(null);
    setDownloading(false);
    mediaStore.clearExportState();
  }, [mediaStore, stopPolling]);

  const handleOpenChange = useCallback(
    (open: boolean) => {
      if (!open) {
        reset();
        onClose();
      }
    },
    [onClose, reset]
  );

  useEffect(() => {
    if (!isOpen) return;
    cancelledRef.current = false;
    stopPolling();
    setPhase('confirm');
    setJob(null);
    setPhaseError(null);
    setDownloading(false);
  }, [isOpen, stopPolling]);

  useEffect(() => () => stopPolling(), [stopPolling]);

  const pollStatus = useCallback(
    async (exportJobId: string) => {
      const result = await mediaStore.getMediaExportStatus(familyId, exportJobId);
      if (cancelledRef.current) return;
      if (!result.success) {
        stopPolling();
        setPhase('failed');
        setPhaseError(errorMessage(result.error, t('exportMedia.errors.statusCheck'), mediaStore.lastError));
        return;
      }
      setJob(result.data);
      if (result.data.status === 'completed') {
        stopPolling();
        setPhase('ready');
      } else if (result.data.status === 'failed') {
        stopPolling();
        setPhase('failed');
        setPhaseError(result.data.errorMessage || t('exportMedia.errors.exportFailed'));
      } else if (result.data.status === 'expired') {
        stopPolling();
        setPhase('expired');
      }
    },
    [familyId, mediaStore, stopPolling, t]
  );

  const handleStartExport = async () => {
    cancelledRef.current = false;
    setPhaseError(null);
    setJob(null);
    setPhase('in_progress');

    const result = await mediaStore.requestMediaExport(familyId);
    if (cancelledRef.current) return;
    if (!result.success) {
      setPhase('confirm');
      setPhaseError(errorMessage(result.error, t('exportMedia.errors.startFailed'), mediaStore.lastError));
      return;
    }

    const exportJobId = result.data.exportJobId;
    await pollStatus(exportJobId);
    if (cancelledRef.current) return;

    pollRef.current = setInterval(() => {
      void pollStatus(exportJobId);
    }, 2500);
  };

  const handleDownload = async () => {
    if (!job?.exportJobId) return;
    setDownloading(true);
    setPhaseError(null);
    try {
      const result = await mediaStore.getMediaExportDownloadUrl(familyId, job.exportJobId);
      if (!result.success) {
        setPhaseError(errorMessage(result.error, t('exportMedia.errors.downloadFailed'), mediaStore.lastError));
        return;
      }
      let url = result.data.url;
      if (!url.startsWith('http')) {
        const config = await getApiConfiguration();
        const base = (config?.basePath || '').replace(/\/$/, '');
        url = `${base}${url.startsWith('/') ? '' : '/'}${url}`;
      }
      window.location.assign(url);
    } finally {
      setDownloading(false);
    }
  };

  const packingLabel =
    job?.status === 'packing'
      ? t('exportMedia.packing')
      : job?.status === 'pending'
        ? t('exportMedia.queued')
        : t('exportMedia.starting');

  return (
    <Dialog.Root open={isOpen} onOpenChange={handleOpenChange}>
      <Dialog.Portal>
        <Dialog.Overlay className="v01-modal-overlay v01-modal-overlay--centered">
          <Dialog.Content
            className="v01-modal v01-modal--panel export-media-modal"
            aria-labelledby="export-media-title"
            aria-describedby="export-media-desc"
          >
            <div className="v01-modal__header v01-modal__header--compact">
              <Dialog.Title id="export-media-title" className="v01-modal__title">
                {t('exportMedia.title')}
              </Dialog.Title>
              <Dialog.Close className="v01-modal__close" aria-label={tCommon('close')}>
                ×
              </Dialog.Close>
            </div>

            <Dialog.Description id="export-media-desc" className="v01-modal__desc">
              {t('exportMedia.description')}
            </Dialog.Description>

            <div className="export-media-modal__body">
              {phase === 'confirm' ? (
                <ul className="export-media-modal__facts">
                  <li>{t('exportMedia.factKeepOpen')}</li>
                  <li>{t('exportMedia.factDownloadZip')}</li>
                  <li>{t('exportMedia.factRetention')}</li>
                  <li>{t('exportMedia.factLimits')}</li>
                </ul>
              ) : null}

              {phaseError ? (
                <div className="v01-modal__error" role="alert">
                  <span>{phaseError}</span>
                </div>
              ) : null}

              {phase === 'in_progress' ? (
                <div className="export-media-modal__progress" role="status" aria-live="polite">
                  <div className="v01-modal__spinner" aria-hidden="true" />
                  <p className="export-media-modal__progress-label">{packingLabel}</p>
                  <p className="export-media-modal__progress-hint">{t('exportMedia.progressHint')}</p>
                </div>
              ) : null}

              {phase === 'ready' && job ? (
                <div className="export-media-modal__ready">
                  <p className="export-media-modal__ready-title">{t('exportMedia.readyTitle')}</p>
                  <p className="export-media-modal__ready-meta">
                    {job.stats
                      ? `${t('exportMedia.filesCount', { count: job.stats.included ?? job.stats.assetCount })}${
                          job.stats.byteTotal ? ` · ${formatBytes(job.stats.byteTotal)}` : ''
                        }`
                      : t('exportMedia.zipReady')}
                    {job.expiresAt
                      ? t('exportMedia.availableUntil', { date: new Date(job.expiresAt).toLocaleDateString() })
                      : ''}
                  </p>
                  <p className="export-media-modal__ready-hint">{t('exportMedia.readyHint')}</p>
                </div>
              ) : null}

              {phase === 'failed' && !phaseError ? (
                <div className="v01-modal__error" role="alert">
                  <span>{job?.errorMessage || t('exportMedia.errors.exportFailed')}</span>
                </div>
              ) : null}

              {phase === 'expired' ? (
                <div className="v01-modal__error" role="alert">
                  <span>{t('exportMedia.expired')}</span>
                </div>
              ) : null}
            </div>

            <div className="v01-modal__footer">
              {phase === 'confirm' ? (
                <>
                  <Dialog.Close className="btn btn--ghost">{tCommon('cancel')}</Dialog.Close>
                  <button type="button" className="btn btn--primary" onClick={() => void handleStartExport()}>
                    {t('actions.startExport')}
                  </button>
                </>
              ) : null}

              {phase === 'in_progress' ? (
                <Dialog.Close className="btn btn--ghost">{tCommon('close')}</Dialog.Close>
              ) : null}

              {phase === 'ready' ? (
                <>
                  <Dialog.Close className="btn btn--ghost">{tCommon('close')}</Dialog.Close>
                  <button
                    type="button"
                    className="btn btn--primary"
                    onClick={() => void handleDownload()}
                    disabled={downloading}
                  >
                    {downloading ? t('actions.preparing') : t('actions.downloadZip')}
                  </button>
                </>
              ) : null}

              {(phase === 'failed' || phase === 'expired') && (
                <>
                  <Dialog.Close className="btn btn--ghost">{tCommon('close')}</Dialog.Close>
                  {phase === 'failed' ? (
                    <button type="button" className="btn btn--primary" onClick={() => void handleStartExport()}>
                      {t('actions.tryAgain')}
                    </button>
                  ) : null}
                </>
              )}
            </div>
          </Dialog.Content>
        </Dialog.Overlay>
      </Dialog.Portal>
    </Dialog.Root>
  );
});

export default ExportMediaModal;
